import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import OtpInput from 'react-otp-input';

import ResentOtpTimer from './ResentOtpTimer';
import { FormGroup } from '../../shared/Form';
import { getRequest, postRequest } from '../../utils/http';
import { LOGIN_URL } from '../../ApiUrls';
import { SMS_MODE } from './constants';
import { TrackDruidEvent, TrackEvent } from '../../utils/analytics';
import {
  EVENT_MOBILE_OTP_FAILED,
  EVENT_MOBILE_OTP_INCORRECT,
  EVENT_MOBILE_OTP_VERIFIED,
} from '../../events';
import { useAppContext } from '../../context/AppContext';
import SpinnerButton from '../../shared/SpinnerButton';
import { RECAPTCHA_SITE_KEY } from '../../constants';

const OTPVerificationForm = ({
  mobile,
  countryCode,
  afterOtpSuccess,
  error,
  setError,
  showOtp,
  setLoading,
  isLoading,
}) => {
  const [otp, setOtp] = useState('');
  const [prevOtp, setPrevOtp] = useState('');
  const otpRef = useRef(null);
  const { business } = useAppContext();
  const [invalidOtp, setInvalidOtp] = useState('');
  const isValidOtp = otp.length === 6 && invalidOtp !== otp;
  const classes = cx('otp-verfication-form', {
    'invalid-otp-verfication-form animate__animated animate__headShake':
      !!invalidOtp,
  });

  const handleResend = (mode = SMS_MODE) => {
    window.grecaptcha.ready(function () {
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        .then(function (token) {
          getRequest({
            url: LOGIN_URL,
            data: {
              mobile,
              country_code: `+${countryCode}`,
              mode,
              hashcode: token,
            },
          });
        });
    });
  };

  const handleVerify = (val) => {
    setLoading(true);
    const payload = { mobile, country_code: `+${countryCode}`, otp: val };
    if (localStorage.getItem('utm_source')) {
      payload.utm_source = localStorage.getItem('utm_source'); // to remove extra double quotes
      payload.utm_medium = localStorage.getItem('utm_medium');
      payload.utm_campaign = localStorage.getItem('utm_campaign');
    }

    postRequest({
      url: LOGIN_URL,
      data: payload,
      headers: {
        mode: 'web',
      },
    })
      .then((res) => {
        const { data, status } = res;
        if (status === 'success') {
          TrackDruidEvent('web', mobile, 'OTP-VERIFIED');
          TrackEvent(EVENT_MOBILE_OTP_VERIFIED, business);
          afterOtpSuccess(data);
        } else {
          setInvalidOtp(val);
          TrackDruidEvent('web', mobile, 'OTP-FAILED');
          TrackEvent(EVENT_MOBILE_OTP_INCORRECT, business);
          setError(['Incorrect OTP. Please try again.']);
          setLoading(false);
        }
      })
      .catch(({ status }) => {
        TrackDruidEvent('web', mobile, 'OTP-FAILED');
        if (status === 400) {
          TrackEvent(EVENT_MOBILE_OTP_INCORRECT, business);
          setInvalidOtp(val);
          setError(['Incorrect OTP. Please try again.']);
        } else if (!status) {
          TrackEvent(EVENT_MOBILE_OTP_FAILED, business);
          setError(['Request timeout. Please try again!']);
        } else {
          TrackEvent(EVENT_MOBILE_OTP_FAILED, business);
          setError(['Something went wrong. Please try again!']);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (otp.length === 6) {
      handleVerify(otp);
    }
    if (otp?.length < prevOtp?.length) otpRef.current.focusInput(otp.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleOtpChange = (val) => {
    setPrevOtp(otp);
    setOtp(val);
    if (invalidOtp) {
      setInvalidOtp('');
    }
    setError([]);
  };

  return (
    <div className={classes}>
      {!!error && <p className="error-alert mb16">{error}</p>}
      <h4 className="section-text-1 pt8 mb6">Verify code</h4>
      <p className="text-1 c-gray-1 mb40">
        Enter verfication code sent to you at
        <br />
        <span className="c-black-1 fw500">
          +{countryCode}-{mobile}
        </span>
      </p>
      <div className="otp-form-content">
        <FormGroup
          labelText={
            <>
              <span>Code</span>
              {showOtp && (
                <ResentOtpTimer handleResend={handleResend} shown={showOtp} />
              )}
            </>
          }
          className="otp-input-wrap"
        >
          <OtpInput
            ref={otpRef}
            value={otp}
            onChange={handleOtpChange}
            numInputs={6}
            isInputNum
            shouldAutoFocus={showOtp}
          />
        </FormGroup>
      </div>
      <SpinnerButton
        showAnimation
        isLoading={isLoading}
        type="submit"
        className="btn-primary-4 full-w"
        disabled={!isValidOtp}
        onClick={(e) => {
          e.preventDefault();
          handleVerify(otp);
        }}
      >
        Verify
      </SpinnerButton>
    </div>
  );
};

export default OTPVerificationForm;
